import React, { useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DatePicker, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash-es';

import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { ReactComponent as LinkedinIcon } from '../../../assets/images/linkedin-icon.svg';
import { ReactComponent as PdfIcon } from '../../../assets/images/pdf-icon-black.svg';
import { ReactComponent as UploadIcon } from '../../../assets/images/uploadIcon.svg';
import { ReactComponent as WebIcon } from '../../../assets/images/web-icon.svg';
import { ReactComponent as WordIcon } from '../../../assets/images/word-icon-black.svg';
import { ReactComponent as CrossIcon } from '../../../assets/images/x.svg';
import { ApplicationFlowStepper } from '../../../components/ApplicationFlowStepper/ApplicationFlowStepper';
import { FormikMultiSelectInput } from '../../../components/FormikMultiSelectInput/FormikMultiSelectInput';
import { FormikTextInput } from '../../../components/FormikTextInput/FormikTextInput';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import Notification from '../../../components/Notification/Notification';
import { OpeningSelect } from '../../../components/OpeningSelect/OpeningSelect';
import { ReferralProgressBar } from '../../../components/ReferralProgressBar/ReferralProgressBar';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import Header from '../../../components/shared/Header/Header';
import LabelWithIcon from '../../../components/shared/Label/LabelWithIcon';
import LocationSelect from '../../../components/shared/Location/LocationSelect';
import { TextArea } from '../../../components/shared/TextArea/TextArea';
import { YourInfoSection } from '../../../components/shared/YourInfoSection/YourInfoSection';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { getCompanyProfile } from '../../../redux/actions/companyProfileActions';
import { setCurrentOrganization } from '../../../redux/actions/organizationActions';
import { useGeneralQuestionMutation, useGetCandidateQuery } from '../../../redux/apis/applicationApi';
import { useGetOrganizationStrengthenQuestionsQuery } from '../../../redux/apis/organizationApi';
import { useGetSkillsQuery } from '../../../redux/apis/referralApi';
import urls from '../../../routing/urls';
import { selectCompanyProfile } from '../../../selectors/companyProfileSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';

import styles from './StrengthenApplication.module.scss';

const getFileIcon = (file) => {
  if (file.type === 'application/pdf' || (file.name && file.name.endsWith('.pdf'))) return <PdfIcon />;
  if (
    (file.type && file.type.includes('word')) ||
    (file.name && (file.name.endsWith('.doc') || file.name.endsWith('.docx')))
  )
    return <WordIcon />;
  return <UploadIcon />;
};

const calculateApplicationStrength = (values, applicationStrengthenQuestions = {}, candidate = null) => {
  let answered = 0;
  let total = 0;

  const check = (enabled, hasValue) => {
    if (enabled) {
      total += 1;
      if (hasValue) answered += 1;
    }
  };

  check(applicationStrengthenQuestions.current_title, !!values.current_title);
  check(applicationStrengthenQuestions.current_employer, !!values.current_employer);
  check(applicationStrengthenQuestions.current_location, !!values.current_location?.full_address);
  check(
    applicationStrengthenQuestions.why_are_they_good_fit,
    (values.current_skills?.length || 0) + (values.new_skills?.length || 0) > 0
  );
  check(applicationStrengthenQuestions.joining_date, !!values.joining_date);
  // Diversity hire: checkbox defaults to unchecked — only count as answered when actively ticked.
  check(
    applicationStrengthenQuestions.diversity_hire,
    values.diversity_hire === 'true' || values.diversity_hire === true
  );

  // Baseline strength: every referral/application starts at 17%. The remaining
  // 83% is earned by answering enabled questions and added on top of the base.
  const BASE_STRENGTH = 17;

  if (total === 0) return 100;
  return Math.min(100, BASE_STRENGTH + Math.round((answered / total) * (100 - BASE_STRENGTH)));
};

const normalizeCandidateLinkedinData = (linkedinData) => ({
  title: linkedinData.position_groups.length > 0 ? linkedinData.position_groups[0].profile_positions[0].title : '',
  location: {
    full_address: linkedinData.location.default,
    country_name: linkedinData.location.country,
    city: linkedinData.location.city,
    state: linkedinData.location.state,
  },
  employer: linkedinData.position_groups.length > 0 ? linkedinData.position_groups[0].profile_positions[0].company : '',
  skills: linkedinData.skills.map((skill, idx) => ({
    id: (idx + 1) * -1,
    name: skill,
    isNew: true,
  })),
});

export const StrengthenApplication = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { companyName, applicationId } = useParams();
  const { isAuthenticated } = useCurrentUser();
  const { data: skillsData } = useGetSkillsQuery();
  const skills = skillsData?.payload?.skills;
  const skillIds = skills?.map((skill) => skill.id);
  const [submitGeneralQuestion, { isLoading: isSubmitting }] = useGeneralQuestionMutation();
  const companyProfile = useSelector(selectCompanyProfile);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const company = companyProfile || currentOrganization;
  const orgId = company?.name === 'Boon' ? company?.id : company?.id || currentOrganization?.id;
  const { data: candidateData } = useGetCandidateQuery({ organizationId: company?.id, applicationId });
  const { data: strengthenQuestionsData } = useGetOrganizationStrengthenQuestionsQuery(
    { organizationId: orgId },
    { skip: !orgId }
  );
  const candidate = candidateData?.payload;
  const applicationStrengthenQuestions = strengthenQuestionsData?.payload || {
    enable_application_questions: true,
    current_title: true,
    current_employer: true,
    current_location: true,
    diversity_hire: true,
    joining_date: true,
    why_are_they_good_fit: true,
  };
  const showApplicationQuestions = applicationStrengthenQuestions.enable_application_questions;
  const hasAnyApplicationQuestionEnabled =
    applicationStrengthenQuestions.enable_application_questions &&
    (applicationStrengthenQuestions.current_title ||
      applicationStrengthenQuestions.current_employer ||
      applicationStrengthenQuestions.current_location ||
      applicationStrengthenQuestions.why_are_they_good_fit ||
      applicationStrengthenQuestions.joining_date ||
      applicationStrengthenQuestions.diversity_hire);
  const showStrengthenStep = !!hasAnyApplicationQuestionEnabled;
  const personalInfo = {
    firstName: candidate?.name?.split(' ')[0],
    lastName: candidate?.name?.split(' ')[1],
    email: candidate?.email,
    avatar_thumb: candidate?.avatar_thumb,
  };
  const isMobile = IsMobile();

  const initialValues = useMemo(() => {
    if (!isEmpty(state?.strength)) {
      return state.strength;
    }

    const linkedInData = isEmpty(state?.response?.linkedin_data)
      ? null
      : normalizeCandidateLinkedinData(state?.response?.linkedin_data);

    return {
      current_title: linkedInData?.title || candidate?.questions?.current_title || '',
      current_employer: linkedInData?.employer || candidate?.questions?.current_employer || '',
      current_location: linkedInData?.location || candidate?.location || null,
      new_skills: linkedInData?.skills || candidate?.skills?.filter((skill) => !skillIds?.includes(skill.id)) || [],
      current_skills: candidate?.skills?.filter((skill) => skillIds?.includes(skill.id)) || [],
      diversity_hire: candidate?.questions?.diversity_hire || '',
      joining_date: candidate?.questions?.joining_date || '',
    };
  }, [candidate, skillIds]);

  const getOrgId = () => {
    if (company?.name === 'Boon') {
      return 'Boon';
    }
    if (company?.id) {
      return company.id;
    }
    if (currentOrganization?.name === 'Boon') {
      return 'Boon';
    }
    return currentOrganization?.id;
  };

  const handleSubmit = (values) => {
    const normalizedCurrentSkills = values.current_skills.map((skill) =>
      typeof skill === 'object' ? skill.id : skill
    );

    const formattedValues = {
      ...values,
      current_skills: normalizedCurrentSkills,
      questionable_type: 'Candidate',
      questionable_id: candidate?.id,
      org_id: getOrgId(),
      job_id: candidate?.job_id,
      diversity_hire: values.diversity_hire === 'true' || values.diversity_hire === true,
    };
    submitGeneralQuestion(formattedValues)
      .unwrap()
      .then((response) => {
        if (response.error) {
          Notification.error(typeof response.error === 'string' ? response.error : response.error.message);
          return;
        }

        const url = companyName ? urls.companyApplicationFlowSuccess : urls.applicationFlowSuccess;
        navigate(url.replace(':companyName', companyName).replace(':applicationId', applicationId), {
          state: {
            ...state,
            applicationStrength: calculateApplicationStrength(values, applicationStrengthenQuestions),
            strength: values,
          },
        });
      });
  };

  useEffect(() => {
    if (companyName) {
      dispatch(getCompanyProfile(companyName, navigate));
    }
  }, [dispatch, companyName, navigate]);

  useEffect(() => {
    if (company) {
      dispatch(setCurrentOrganization(company));
    }
  }, [company]);

  useEffect(() => {
    if (orgId && strengthenQuestionsData !== undefined && !showStrengthenStep) {
      const url = companyName ? urls.companyApplicationFlowSuccess : urls.applicationFlowSuccess;
      navigate(url.replace(':companyName', companyName || '').replace(':applicationId', applicationId));
    }
  }, [orgId, strengthenQuestionsData, showStrengthenStep, companyName, applicationId, navigate]);

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('apply')}
          backButton={isAuthenticated && !companyName ? t('back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.container}>
          <ApplicationFlowStepper currentStep={2} showStrengthenStep={showStrengthenStep} />

          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2>{t('application.applyTo', { company: company?.name })}</h2>
            </div>

            <div className={styles.previousInfo}>
              <div className="space-y-[6px]">
                <label htmlFor="opening" className={styles.formLabel}>
                  {t('referral:openingInfo')}
                </label>
                <OpeningSelect
                  organizationId={company?.name === 'Boon' ? 'Boon' : company?.id}
                  value={candidate?.job_id}
                  isDisabled
                />
              </div>
              <TextArea
                label={t('referral:reviewPage.messageToEmployer')}
                placeholder={t('placeholders.defaultReferralMessage')}
                maxLength={300}
                showCharacterCount
                rows={4}
                value={candidate?.notes}
                disabled
              />
              <YourInfoSection personalInfo={personalInfo} />
              <div className="flex items-center gap-[8px] flex-wrap">
                {candidate?.resume_file_links?.map((license) => (
                  <Tooltip title={license.name}>
                    <span className={styles.urlInputIcon}>{getFileIcon(license)}</span>
                  </Tooltip>
                ))}
                {candidate?.links_json
                  ?.filter((link) => link.value && link.value !== 'www.linkedin.com/in/')
                  ?.map((link) => (
                    <Tooltip title={link.value}>
                      <span className={styles.urlInputIcon}>
                        {link.type === 'ln' ? <LinkedinIcon className="w-[20px] h-[20px]" /> : <WebIcon />}
                      </span>
                    </Tooltip>
                  ))}
              </div>
            </div>
            <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
              {({ values, setFieldValue }) => {
                const applicationStrength = calculateApplicationStrength(values, applicationStrengthenQuestions);

                return (
                  <Form>
                    <div className={styles.form}>
                      <p className={styles.strengthenApplication}>{t('application.strengthenApplication')}</p>
                      {showApplicationQuestions && applicationStrengthenQuestions.current_title && (
                        <FormikTextInput
                          name="current_title"
                          label={t('strengthen.currentTitleQuestion')}
                          placeholder={t('placeholders.currentTitle')}
                        />
                      )}
                      {showApplicationQuestions && applicationStrengthenQuestions.current_employer && (
                        <FormikTextInput
                          name="current_employer"
                          label={t('strengthen.currentEmployerQuestion')}
                          placeholder={t('placeholders.currentEmployer')}
                        />
                      )}
                      {showApplicationQuestions && applicationStrengthenQuestions.current_location && (
                        <div>
                          <label className={styles.formLabel} htmlFor="current_location">
                            {t('strengthen.currentLocation')}
                          </label>
                          <LocationSelect
                            value={values.current_location}
                            onChange={(value) => setFieldValue('current_location', value)}
                            placeholder={t('placeholders.location')}
                            disableFloatingLabel
                            isApplicationFlow
                          />
                        </div>
                      )}

                      {showApplicationQuestions && applicationStrengthenQuestions.why_are_they_good_fit && (
                        <FormikMultiSelectInput
                          name="current_skills"
                          label={t('application.whatGoodAt')}
                          options={(skills || []).map((skill) => ({
                            label: skill.name,
                            value: skill.id,
                            id: skill.id,
                            name: skill.name,
                          }))}
                          isMulti
                          getOptionValue={(option) => option.id}
                          getOptionLabel={(option) => option.name}
                          controlShouldRenderValue={false}
                          isClearable={false}
                          placeholder={t('placeholders.selectSkills')}
                        />
                      )}
                      <div className={styles.currentSkillsContainer}>
                        {values.new_skills.map((skill) => (
                          <LabelWithIcon
                            key={skill.id}
                            customStyles={styles.currentSkill}
                            tag={skill.name}
                            onDelete={() =>
                              setFieldValue(
                                'new_skills',
                                values.new_skills.filter((s) => s.id !== skill.id)
                              )
                            }
                            deleteIcon={<CrossIcon />}
                          />
                        ))}
                        {values.current_skills.map((skillId) => {
                          const actualSkillId = typeof skillId === 'object' ? skillId.id : skillId;
                          const skill = skills?.find((s) => s.id === actualSkillId);
                          const skillName = skill?.name || (typeof skillId === 'object' ? skillId.name : skillId);
                          const skillKey = typeof skillId === 'object' ? skillId.id : skillId;

                          return (
                            <LabelWithIcon
                              key={skillKey}
                              customStyles={styles.currentSkill}
                              tag={skillName}
                              onDelete={() =>
                                setFieldValue(
                                  'current_skills',
                                  values.current_skills.filter((s) => {
                                    const sId = typeof s === 'object' ? s.id : s;
                                    const skillIdToCompare = typeof skillId === 'object' ? skillId.id : skillId;
                                    return sId !== skillIdToCompare;
                                  })
                                )
                              }
                              deleteIcon={<CrossIcon />}
                            />
                          );
                        })}
                      </div>
                      {showApplicationQuestions && applicationStrengthenQuestions.joining_date && (
                        <div className={styles.formGroup}>
                          <label htmlFor="joining_date" className={styles.formLabel}>
                            {t('strengthen.soonestStartDateQuestion')}
                          </label>
                          <div>
                            <DatePicker
                              value={values.joining_date ? dayjs(values.joining_date) : null}
                              onChange={(date) => setFieldValue('joining_date', date?.toDate())}
                              className={styles.input}
                              format="MM/DD/YYYY"
                              placeholder={t('placeholders.dateFormat')}
                            />
                          </div>
                        </div>
                      )}

                      {showApplicationQuestions && applicationStrengthenQuestions.diversity_hire && (
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel} htmlFor="diversity_hire">
                            {t('application.diversityPersonQuestion')}
                          </label>
                          <div className={styles.diversityCheckbox}>
                            <input
                              type="checkbox"
                              name="diversity_hire"
                              checked={values.diversity_hire === 'true'}
                              onChange={(e) => setFieldValue('diversity_hire', e.target.checked ? 'true' : 'false')}
                            />
                            <span>{t('yes')}</span>
                          </div>
                        </div>
                      )}
                      {hasAnyApplicationQuestionEnabled && (
                        <ReferralProgressBar
                          title={t('application.applicationStrength')}
                          applicationStrength={applicationStrength}
                        />
                      )}
                    </div>
                    <div className={styles.nextButtonContainer}>
                      <ButtonWithIcon
                        type="submit"
                        variant="secondary-36"
                        text={
                          <span className="flex items-center gap-[8px]">
                            {isSubmitting && <Spinner animation="border" size="sm" />} {t('buttons.sendNow')}
                          </span>
                        }
                        textClassName="text-white"
                        containerClass="w-[142px] self-end"
                        disabled={isSubmitting}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};
